import { Add as AddIcon, Delete as DeleteIcon, Sync as SyncIcon } from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    bulkLinkUsers,
    fetchCustomer,
    linkUserToCustomer,
    unlinkUserFromCustomer,
    updateCustomer,
} from "../../../../../api/customers";
import { fetchProfiles } from "../../../../../api/profile";
import { quickbooksService } from "../../../../../api/quickbooks";
import {
    getDisplayAccountType,
    mapFrontendToBackend,
} from "../../../../../utils/accountTypeUtils";
import { getAllowedAccountRoles } from "../../../../../utils/userRoleUtils";

const ACCOUNT_TYPE_OPTIONS = [
  "Trial",
  "Onboarding",
  "Ongoing",
  "Consolidated",
  "Aggregated",
  "Demo",
];

const mapProfileToInternalUserOption = (profile) => {
  const firstName = profile.user.first_name || "";
  const lastName = profile.user.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const displayName = fullName || profile.user.email;

  return {
    id: profile.id,
    name: displayName,
    email: profile.user.email,
    label: displayName,
    value: profile.user.email,
    firstName,
    lastName,
    type: profile.type,
  };
};

export default function AccountEditDialog({ data, onClose, onSubmit, onDeleteRequest, onShowSuccess }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    accountName: "",
    accountType: "",
    primaryContact: "",
    primaryContactEmail: "",
    manager: "",
    bookkeeper: "",
  });

  const [alternativeUsers, setAlternativeUsers] = useState([]);
  const [newUserName, setNewUserName] = useState("");
  const [newUserType, setNewUserType] = useState("");
  const [quickbooksStatus, setQuickbooksStatus] = useState("disconnected");

  // State for user search functionality
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [originalData, setOriginalData] = useState({});
  const [originalAlternativeUsers, setOriginalAlternativeUsers] = useState([]);
  const [forceUpdate, setForceUpdate] = useState(0);


  // State for bookkeeper search
  const [bookkeepers, setBookkeepers] = useState([]);
  const [selectedBookkeeper, setSelectedBookkeeper] = useState(null);
  const [loadingBookkeepers, setLoadingBookkeepers] = useState(false);
const [bookkeeperOpen, setBookkeeperOpen] = useState(false);

  // State for manager search
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [loadingManagers, setLoadingManagers] = useState(false);
const [managerOpen, setManagerOpen] = useState(false);

  const accountRoleOptions = useMemo(() => ["Lead", "Customer", "Viewer", "Accountant"], []);

  const handleAlternativeUserChange = useCallback((index, field, value) => {
    setAlternativeUsers((prev) =>
      prev.map((user, userIndex) =>
        userIndex === index
          ? {
              ...user,
              [field]: value,
            }
          : user
      )
    );
    setForceUpdate((prev) => prev + 1);
  }, []);

  useEffect(() => {
    // Fetch full customer data with profiles when dialog opens
    const fetchFullCustomerData = async () => {
      if (data && data.id) {
        try {
          const fullCustomer = await fetchCustomer(data.id);
          initializeDialogData(fullCustomer);
        } catch (error) {
          console.error("Error fetching full customer data:", error);
          // Fall back to using existing data if fetch fails
          initializeDialogData(data.originalCustomer);
        }
      }
    };

    const initializeDialogData = (customer) => {
      if (!customer) return;

      // Use getDisplayAccountType utility to get the correct display value
      const displayAccountType = getDisplayAccountType(customer) || data.accountType;

      const initialFormData = {
        accountName: data.accountName || "",
        accountType: displayAccountType,
        primaryContact: data.primaryContact || "",
        primaryContactEmail: data.primaryEmail || "",
        manager: data.manager || "",
        bookkeeper: data.bookkeeper || "",
      };

      // Use profiles_with_roles from ProfileCustomers
      // Filter to only show external users and exclude the primary contact
      const externalUserTypes = ['external_accountant', 'client', 'lead', 'viewer'];
      const primaryContactName = data.primaryContact || "";

      const initialAlternativeUsers =
        customer?.profiles_with_roles
          ?.filter((profileWithRole) => {
            // Only include external user types
            if (!externalUserTypes.includes(profileWithRole.profile_type)) {
              return false;
            }
            // Exclude the primary contact by name
            const profileName = profileWithRole.name || profileWithRole.email || "";
            return profileName.trim().toLowerCase() !== primaryContactName.trim().toLowerCase();
          })
          ?.map((profileWithRole) => {
            const roleMap = {
              "External Accountant": "Accountant",
            };

            let normalizedRole = roleMap[profileWithRole.role] || profileWithRole.role;
            if (!accountRoleOptions.includes(normalizedRole)) {
              normalizedRole = "Customer";
            }

            return {
              name: profileWithRole.name || profileWithRole.email || "Incomplete Profile",
              type: normalizedRole,
              profileId: profileWithRole.id,
              profileType: profileWithRole.profile_type, // Store the user's actual type
            };
          }) || [];

      setFormData(initialFormData);
      setAlternativeUsers(initialAlternativeUsers);
      setQuickbooksStatus(data.quickbooksStatus || "disconnected");

      setOriginalData(initialFormData);
      setOriginalAlternativeUsers(initialAlternativeUsers);
    };

    if (data) {
      fetchFullCustomerData();
    }
  }, [data, accountRoleOptions]);

const searchBookkeepers = useCallback(async (searchTerm = "") => {
    setLoadingBookkeepers(true);
    try {
    const params = {
      category: "Internal",
      page_size: 100,
    };
    if (searchTerm && searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

      const response = await axios.get("/api/v2/profiles/", {
        headers: { "Content-Type": "application/json" },
      params,
        withCredentials: true,
      });

    const bookkeeperOptions = (response.data.results || []).map(
      mapProfileToInternalUserOption
    );

      setBookkeepers(bookkeeperOptions);
    } catch (error) {
      setBookkeepers([]);
    } finally {
      setLoadingBookkeepers(false);
    }
  }, []);

const searchManagers = useCallback(async (searchTerm = "") => {
    setLoadingManagers(true);
    try {
    const params = {
      category: "Internal",
      page_size: 100,
    };
    if (searchTerm && searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

      const response = await axios.get("/api/v2/profiles/", {
        headers: { "Content-Type": "application/json" },
      params,
        withCredentials: true,
      });

    const managerOptions = (response.data.results || []).map(
      mapProfileToInternalUserOption
    );

      setManagers(managerOptions);
    } catch (error) {
      setManagers([]);
    } finally {
      setLoadingManagers(false);
    }
  }, []);

// Prefetch manager and bookkeeper lists on mount
useEffect(() => {
  searchManagers();
  searchBookkeepers();
}, [searchManagers, searchBookkeepers]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setForceUpdate(prev => prev + 1); // Force re-render
  };

  const handleAddUser = () => {
    if (newUserName && newUserType) {
      // Map "Accountant" to "External Accountant" for backend
      const mappedType = newUserType === "Accountant" ? "External Accountant" : newUserType;
      const newUser = {
        name: newUserName,
        type: mappedType,
        profileId: selectedUser?.id, // Include profileId if user was selected from dropdown
        profileType: selectedUser?.type // Store the user's actual profile type for role filtering
      };
      setAlternativeUsers([...alternativeUsers, newUser]);
      setNewUserName("");
      setNewUserType("");
      setSelectedUser(null);
      setForceUpdate(prev => prev + 1); // Force re-render
    }
  };

  const handleDeleteUser = (index) => {
    setAlternativeUsers(alternativeUsers.filter((_, i) => i !== index));
    setForceUpdate(prev => prev + 1); // Force re-render
  };

  const handleCreateNewUser = () => {
    onClose();
    navigate('/management/users');
    setTimeout(() => {
      const event = new CustomEvent('openUserCreateDialog');
      window.dispatchEvent(event);
    }, 100);
  };

  const fetchUsersList = async (searchTerm = "") => {
    if (!searchTerm || searchTerm.length < 2) {
      setAvailableUsers([]);
      setLoadingUsers(false);
      return;
    }

    setLoadingUsers(true);
    try {
      const response = await fetchProfiles({
        search: searchTerm,
        page_size: 10,
        external_only: true  // Only fetch external users (excludes Admin, Manager, Bookkeeper, Accountant)
      });
      const users = response.results || response;

      const transformedUsers = users.map(user => ({
        id: user.id,
        optionType: "user",
        name: user.user?.first_name && user.user?.last_name
          ? `${user.user.first_name} ${user.user.last_name}`.trim()
          : user.user?.email || user.email,
        email: user.user?.email || user.email,
        type: user.type
      }));

      if (transformedUsers.length === 0) {
        setAvailableUsers([
          {
            id: "__no_user_results__",
            optionType: "noResults",
            searchTerm
          }
        ]);
      } else {
        setAvailableUsers(transformedUsers);
      }
    } catch (error) {
      setAvailableUsers([
        {
          id: "__no_user_results__",
          optionType: "noResults",
          searchTerm
        }
      ]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const validateForm = useCallback(() => {
    // Only validate fields that are actually editable and required
    // Account Name, Primary Contact, and Primary Contact Email are disabled (read-only)
    if (!formData.accountType.trim()) {
      return false;
    }
    // Manager and bookkeeper are optional fields

    // Alternative users validation - only validate users that have both name and type
    // Skip validation for users that are linked profiles (they come from the database)
    for (const user of alternativeUsers) {
      if (user.profileId) {
        continue;
      }
      if (user.name.trim() && !user.type.trim()) {
        return false;
      }
      if (!user.name.trim() && user.type.trim()) {
        return false;
      }
    }

    return true;
  }, [formData.accountType, alternativeUsers]);

  const hasChanges = useCallback(() => {
    const formChanged = Object.keys(originalData).some(
      key => originalData[key] !== formData[key]
    );

    // Compare alternative users properly - check if arrays are different
    const alternativeUsersChanged =
      originalAlternativeUsers.length !== alternativeUsers.length ||
      // Check if any original user is missing from current users
      originalAlternativeUsers.some(originalUser =>
        !alternativeUsers.some(currentUser =>
          originalUser.name === currentUser.name &&
          originalUser.type === currentUser.type &&
          originalUser.profileId === currentUser.profileId
        )
      ) ||
      // Check if any current user is not in original users
      alternativeUsers.some(currentUser =>
        !originalAlternativeUsers.some(originalUser =>
          originalUser.name === currentUser.name &&
          originalUser.type === currentUser.type &&
          originalUser.profileId === currentUser.profileId
        )
      );


    return formChanged || alternativeUsersChanged;
  }, [formData, originalData, alternativeUsers, originalAlternativeUsers]);


  const isFormValidAndChanged = useMemo(() => {
    const isValid = validateForm();
    const hasChangesResult = hasChanges();
    return isValid && hasChangesResult;
  }, [validateForm, hasChanges]);


  const handleSubmit = async () => {
    try {
      const originalProfileIds = originalAlternativeUsers
        .map((user) => user.profileId)
        .filter(Boolean);
      const currentProfileIds = alternativeUsers
        .filter(user => user.profileId) // Only users that have profileId (linked users)
        .map(user => user.profileId);

      // Find users to unlink (in original but not in current)
      const usersToUnlink = originalProfileIds.filter(id => !currentProfileIds.includes(id));

      // Find users to link (in current but not in original)
      const usersToLink = currentProfileIds.filter(id => !originalProfileIds.includes(id));

      const originalRoleLookup = originalAlternativeUsers.reduce((acc, user) => {
        if (user.profileId) {
          const normalized = user.type === "Accountant" ? "Accountant" : user.type;
          acc[user.profileId] = normalized || "Customer";
        }
        return acc;
      }, {});

      const roleChangeProfiles = alternativeUsers
        .filter((user) => {
          if (!user.profileId) {
            return false;
          }

          const previousRole = originalRoleLookup[user.profileId];
          const currentRole = user.type === "Accountant" ? "Accountant" : user.type || "Customer";

          return previousRole && previousRole !== currentRole;
        })
        .map((user) => user.profileId);

      // Create profile_roles object mapping profileId to role
      const profileRoles = {};
      alternativeUsers.forEach(user => {
        if (user.profileId) {
          const mappedRole =
            user.type === "Accountant" ? "External Accountant" : user.type || "Customer";
          profileRoles[user.profileId] = mappedRole;
        }
      });

      // Use bulk endpoint for linking/unlinking users with roles
      const bulkUsersToLink = Array.from(new Set([...usersToLink, ...roleChangeProfiles]));

      const profilesChanged = usersToUnlink.length > 0 || bulkUsersToLink.length > 0;

      if (profilesChanged) {
        try {
          await bulkLinkUsers(data.id, bulkUsersToLink, usersToUnlink, profileRoles);

        } catch (error) {
          // Fallback to individual calls if bulk fails
          if (usersToUnlink.length > 0) {
            const unlinkPromises = usersToUnlink.map(profileId =>
              unlinkUserFromCustomer(data.id, profileId).catch(() => {
                return { hasError: true, profileId };
              })
            );
            await Promise.all(unlinkPromises);
          }

          if (bulkUsersToLink.length > 0) {
            const linkPromises = bulkUsersToLink.map(profileId =>
              linkUserToCustomer(data.id, profileId).catch(() => {
                return { hasError: true, profileId };
              })
            );
            await Promise.all(linkPromises);
          }
        }
      }

      // Then update other customer data
      const submitData = {};
      const trimmedAccountName = formData.accountName.trim();
      if (trimmedAccountName !== (originalData.accountName || "").trim()) {
        submitData.name = trimmedAccountName;
      }

      const trimmedPrimaryContact = formData.primaryContact.trim();
      if (trimmedPrimaryContact !== (originalData.primaryContact || "").trim()) {
        submitData.primary_contact = trimmedPrimaryContact;
      }

      const trimmedEmail = formData.primaryContactEmail.trim();
      if (trimmedEmail !== (originalData.primaryContactEmail || "").trim()) {
        submitData.email = trimmedEmail;
      }

      if (formData.accountType !== originalData.accountType) {
        const backendType = mapFrontendToBackend(formData.accountType);
        if (backendType) {
          submitData.customer_type = backendType;
        }
        submitData.frontend_account_type = formData.accountType;
      }

      const trimmedManager = formData.manager.trim();
      if (trimmedManager !== (originalData.manager || "").trim()) {
        submitData.manager = trimmedManager || null;
      }

      const trimmedBookkeeper = formData.bookkeeper.trim();
      if (trimmedBookkeeper !== (originalData.bookkeeper || "").trim()) {
        submitData.bookkeeper = trimmedBookkeeper || null;
      }

      let updatedCustomer = data.originalCustomer;
      if (Object.keys(submitData).length > 0) {
        updatedCustomer = await updateCustomer(data.id, submitData);
      }

      if (profilesChanged) {
        try {
          updatedCustomer = await fetchCustomer(data.id);
        } catch (error) {
          // If refresh fails, keep the last known customer data
        }
      }

      if (onSubmit) {
        onSubmit(updatedCustomer);
      }

      // Show success message before closing dialog
      if (onShowSuccess) {
        onShowSuccess("Account updated successfully!");
      }

      onClose();
    } catch (error) {
      // Error handled silently
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setAlternativeUsers(originalAlternativeUsers);
    setNewUserName("");
    setNewUserType("");
    onClose();
  };

  const handleDelete = () => {
    if (!onDeleteRequest) {
      onShowSuccess?.("Failed to delete account. Please try again.", "error");
      return;
    }

    onDeleteRequest(data);
    onClose();
  };

  const handleQBConnect = async () => {
    try {
      // Store customer ID for OAuth flow
      sessionStorage.setItem("customer_id", data.customerId);
      sessionStorage.setItem("qb_connect_origin", "accountmanagement");
      sessionStorage.setItem("qb_connect_page_params", window.location.search || "");

      // Get the QuickBooks authorization URL
      const response = await quickbooksService.getAuthUrl(data.customerId);

      if (response.data && response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        alert("Failed to get QuickBooks authorization URL");
      }
    } catch (error) {
      alert("Failed to connect to QuickBooks");
    }
  };

  return (
    <Dialog
      open={true}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
      sx={{
        zIndex: 9999,
        "& .MuiAutocomplete-popper": {
          zIndex: 999999
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Edit Account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Here, you can view account details, track connections, and edit essentials.
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3, maxHeight: '60vh', overflow: 'auto' }}>
        <Stack spacing={3}>
           <Box>
             <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
               Primary Fields
             </Typography>

             <Grid container rowSpacing={3} columnSpacing={2}>
               <Grid size={6}>
                <TextField
                  label="Account Name"
                  value={formData.accountName}
                  fullWidth
                  size="small"
                  onChange={handleChange("accountName")}
                />
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    value={formData.accountType}
                    onChange={handleChange("accountType")}
                    label="Account Type"
                    MenuProps={{
                      disablePortal: true,
                      PaperProps: {
                        style: {
                          zIndex: 99999,
                        },
                      },
                    }}
                  >
                    {ACCOUNT_TYPE_OPTIONS.map((accountType) => (
                      <MenuItem key={accountType} value={accountType}>
                        {accountType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

               <Grid size={6}>
                <TextField
                  label="Primary Contact"
                  value={formData.primaryContact}
                  fullWidth
                  size="small"
                  onChange={handleChange("primaryContact")}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Primary Contact Email"
                  type="email"
                  value={formData.primaryContactEmail}
                  fullWidth
                  size="small"
                  onChange={handleChange("primaryContactEmail")}
                />
              </Grid>

               <Grid size={6}>
                <Autocomplete
                  freeSolo
                  options={managers}
                  value={selectedManager}
                  inputValue={formData.manager}
                  loading={loadingManagers}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, manager: newInputValue || "" }));
                      setSelectedManager(null);
                      searchManagers(newInputValue || "");
                    } else if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, manager: "" }));
                      setSelectedManager(null);
                      searchManagers("");
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    if (newValue && typeof newValue === 'object') {
                      setSelectedManager(newValue);
                      setFormData(prev => ({
                        ...prev,
                        manager: newValue.name
                      }));
                    } else if (newValue === null || newValue === '') {
                      setSelectedManager(null);
                      setFormData(prev => ({ ...prev, manager: "" }));
                    }
                  }}
                  filterOptions={(options) => options}
                  open={managerOpen}
                  onOpen={() => {
                    setManagerOpen(true);
                    if (managers.length === 0 && !loadingManagers) {
                      searchManagers(formData.manager || "");
                    }
                  }}
                  onClose={() => {
                    setManagerOpen(false);
                  }}
                  selectOnFocus
                  clearOnBlur={false}
                  handleHomeEndKeys
                  loadingText="Loading internal users..."
                  noOptionsText={
                    formData.manager && formData.manager.trim().length > 0
                      ? "No internal users found"
                      : "Type to search internal users..."
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Manager"
                      size="small"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const uniqueKey = `manager-${option.id}`;
                    return (
                      <li key={uniqueKey} {...otherProps}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {option.firstName || option.lastName
                              ? `${option.firstName} ${option.lastName}`.trim()
                              : option.email
                            }
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>
                            {option.email} • {option.type}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  PopperProps={{
                    placement: 'bottom-start',
                    style: {
                      zIndex: 999999,
                      position: 'fixed',
                      maxHeight: '200px',
                      overflow: 'auto'
                    },
                    modifiers: [
                      {
                        name: 'flip',
                        enabled: false,
                      },
                      {
                        name: 'preventOverflow',
                        enabled: false,
                      },
                    ],
                  }}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                      overflow: 'auto'
                    }
                  }}
                  componentsProps={{
                    popper: {
                      style: {
                        zIndex: 999999,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <Autocomplete
                  freeSolo
                  options={bookkeepers}
                  value={selectedBookkeeper}
                  inputValue={formData.bookkeeper}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, bookkeeper: newInputValue }));
                      setSelectedBookkeeper(null);
                      searchBookkeepers(newInputValue || "");
                    } else if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, bookkeeper: "" }));
                      setSelectedBookkeeper(null);
                      searchBookkeepers("");
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    if (newValue && typeof newValue === 'object') {
                      setSelectedBookkeeper(newValue);
                      setFormData(prev => ({
                        ...prev,
                        bookkeeper: newValue.name
                      }));
                    } else if (newValue === null || newValue === '') {
                      setSelectedBookkeeper(null);
                      setFormData(prev => ({ ...prev, bookkeeper: "" }));
                    }
                  }}
                  filterOptions={(options) => options}
                  open={bookkeeperOpen}
                  onOpen={() => {
                    setBookkeeperOpen(true);
                    if (bookkeepers.length === 0 && !loadingBookkeepers) {
                      searchBookkeepers(formData.bookkeeper || "");
                    }
                  }}
                  onClose={() => {
                    setBookkeeperOpen(false);
                  }}
                  selectOnFocus
                  clearOnBlur={false}
                  handleHomeEndKeys
                  loading={loadingBookkeepers}
                  loadingText="Loading internal users..."
                  noOptionsText={
                    formData.bookkeeper && formData.bookkeeper.trim().length > 0
                      ? "No internal users found"
                      : "Type to search internal users..."
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bookkeeper"
                      size="small"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const uniqueKey = `bookkeeper-${option.id}`;
                    return (
                      <li key={uniqueKey} {...otherProps}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {option.firstName || option.lastName
                              ? `${option.firstName} ${option.lastName}`.trim()
                              : option.email
                            }
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>
                            {option.email} • {option.type}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  PopperProps={{
                    disablePortal: true,
                    placement: 'bottom-start',
                    style: {
                      zIndex: 999999,
                      position: 'fixed',
                      maxHeight: '200px',
                      overflow: 'auto'
                    },
                    modifiers: [
                      {
                        name: 'flip',
                        enabled: false,
                      },
                      {
                        name: 'preventOverflow',
                        enabled: false,
                      },
                    ],
                  }}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                      overflow: 'auto'
                    }
                  }}
                  componentsProps={{
                    popper: {
                      style: {
                        zIndex: 999999,
                      },
                    },
                  }}
                />
              </Grid>
             </Grid>
           </Box>

           <Box>
             <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
               Alternative Users
             </Typography>

             {alternativeUsers.map((user, index) => {
               // Get allowed roles for this user's profile type
               const allowedRoles = user.profileType ? getAllowedAccountRoles(user.profileType) : accountRoleOptions;

               return (
               <Grid container columnSpacing={2} key={index} sx={{ mb: 2 }}>
                 <Grid size={6}>
                   <TextField
                     label="User Name"
                     value={user.name}
                     fullWidth
                     size="small"
                     onChange={(event) =>
                       handleAlternativeUserChange(index, "name", event.target.value)
                     }
                   />
                 </Grid>
                 <Grid size={6}>
                   <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                     <FormControl fullWidth variant="outlined" size="small">
                       <InputLabel>User Type</InputLabel>
                       <Select
                         value={user.type}
                         label="User Type"
                         onChange={(event) =>
                           handleAlternativeUserChange(index, "type", event.target.value)
                         }
                         MenuProps={{
                           disablePortal: true,
                           PaperProps: {
                             style: {
                               zIndex: 99999,
                             },
                           },
                         }}
                       >
                         {allowedRoles.map((type) => (
                           <MenuItem key={type} value={type}>
                             {type}
                           </MenuItem>
                         ))}
                       </Select>
                     </FormControl>
                     <IconButton
                       size="small"
                       onClick={() => handleDeleteUser(index)}
                       sx={{ color: "#ef4444" }}
                     >
                       <DeleteIcon fontSize="small" />
                     </IconButton>
                   </Box>
                 </Grid>
               </Grid>
               );
             })}

             <Grid container columnSpacing={2} sx={{ mb: 2 }}>
               <Grid size={6}>
                 <Autocomplete
                   freeSolo
                   options={availableUsers}
                   getOptionLabel={(option) => {
                     if (typeof option === 'string') return option;
                    if (option.optionType === 'noResults') return option.searchTerm || '';
                    return option.name || '';
                   }}
                   isOptionEqualToValue={(option, value) => {
                     if (!option || !value) return false;
                     return option.id === value.id;
                   }}
                   value={newUserName ? availableUsers.find(user => user.name === newUserName) || newUserName : null}
                   inputValue={newUserName}
                   onInputChange={(event, newValue, reason) => {
                     if (reason === 'input') {
                       setNewUserName(newValue || "");
                       if (newValue && newValue.length >= 2) {
                         fetchUsersList(newValue);
                       } else {
                         setAvailableUsers([]);
                       }
                     }
                   }}
                  onChange={(event, newValue, reason) => {
                    if (newValue && typeof newValue === 'object') {
                      if (newValue.optionType === 'noResults') {
                        handleCreateNewUser();
                        setAvailableUsers([]);
                        setNewUserName("");
                        setNewUserType("");
                        return;
                      }
                      setNewUserName(newValue.name);
                      setSelectedUser(newValue);
                      setAvailableUsers([]);
                    } else if (newValue === null || newValue === '') {
                      setNewUserName("");
                      setSelectedUser(null);
                      setAvailableUsers([]);
                    }
                  }}
                  filterOptions={(options) => options}
                   loading={loadingUsers}
                   loadingText="Searching users..."
                   renderInput={(params) => (
                     <TextField
                       {...params}
                       label="User Name"
                       size="small"
                       fullWidth
                     />
                   )}
                  renderOption={(props, option) => {
                    if (option.optionType === 'noResults') {
                      return (
                        <li {...props} key={option.id}>
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                              No existing user matches "<em>{option.searchTerm}</em>"
                            </Typography>
                            <Divider />
                            <Box
                              sx={{ px: 2, py: 1, cursor: 'pointer' }}
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                handleCreateNewUser();
                                setAvailableUsers([]);
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#627CFB', fontWeight: 500 }}>
                                + Create New User
                              </Typography>
                            </Box>
                          </Box>
                        </li>
                      );
                    }

                    const { key, ...otherProps } = props;
                    return (
                      <li key={option.id} {...otherProps}>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            {option.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {option.email} • {option.type.charAt(0).toUpperCase() + option.type.slice(1).toLowerCase()}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                 noOptionsText="Type at least 2 characters to search users..."
                   PopperProps={{
                     placement: 'bottom-start',
                     style: {
                       zIndex: 999999,
                       position: 'fixed'
                     },
                     modifiers: [
                       {
                         name: 'flip',
                         enabled: false,
                       },
                       {
                         name: 'preventOverflow',
                         enabled: false,
                       },
                     ],
                   }}
                   componentsProps={{
                     popper: {
                       style: {
                         zIndex: 999999,
                       },
                     },
                   }}
                 />
               </Grid>
               <Grid size={6}>
                 <FormControl fullWidth variant="outlined" size="small">
                   <InputLabel>User Type</InputLabel>
                   <Select
                     value={newUserType}
                     onChange={(e) => setNewUserType(e.target.value)}
                     label="User Type"
                     MenuProps={{
                       disablePortal: true,
                       PaperProps: {
                         style: {
                           zIndex: 99999,
                         },
                       },
                     }}
                   >
                     <MenuItem value="">
                       <em>Select User Type</em>
                     </MenuItem>
                     {(selectedUser?.type ? getAllowedAccountRoles(selectedUser.type) : accountRoleOptions).map((type) => (
                       <MenuItem key={type} value={type}>
                         {type}
                       </MenuItem>
                     ))}
                   </Select>
                 </FormControl>
               </Grid>
             </Grid>

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              disabled={!newUserName || !newUserType}
              sx={{
                color: "#627CFB !important",
                textTransform: "none",
                fontWeight: 500,
                border: (newUserName && newUserType) ? "1.5px solid #627CFB" : "1.5px solid transparent",
                transition: "all 0.2s ease-in-out",
                "& .MuiButton-startIcon": {
                  color: "#627CFB !important",
                },
                "&:hover": {
                  backgroundColor: "rgba(98, 124, 251, 0.04)",
                  border: (newUserName && newUserType) ? "1.5px solid #627CFB" : "1.5px solid transparent",
                },
              }}
            >
              Add User
            </Button>
           </Box>

           <Box>
             <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
               Quickbooks Status
             </Typography>

             <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
               <Chip
                 label={
                   quickbooksStatus === "Connected"
                     ? "Connected"
                     : quickbooksStatus === "Not Required"
                     ? "Not Required"
                     : "Disconnected"
                 }
                 sx={
                   quickbooksStatus === "Connected"
                     ? {
                         backgroundColor: "#dcfce7",
                         color: "#000000",
                         border: "1px solid #bbf7d0",
                         fontWeight: 500,
                       }
                     : quickbooksStatus === "Not Required"
                     ? {
                         backgroundColor: "#f3f4f6",
                         color: "#000000",
                         border: "1px solid #d1d5db",
                         fontWeight: 500,
                       }
                     : {
                         backgroundColor: "#fee2e2",
                         color: "#000000",
                         border: "1px solid #fecaca",
                         fontWeight: 500,
                       }
                 }
               />
               {quickbooksStatus !== "Connected" && (
                 <Button
                   startIcon={<SyncIcon />}
                   onClick={handleQBConnect}
                   sx={{
                     color: "#627CFB",
                     textTransform: "none",
                     fontWeight: 500,
                     "&:hover": {
                       backgroundColor: "rgba(98, 124, 251, 0.04)",
                     },
                   }}
                 >
                   Connect QuickBooks
                 </Button>
               )}
             </Box>
           </Box>
         </Stack>
       </DialogContent>

       <DialogActions sx={{ p: 3, pt: 1, position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 10 }}>
         <Button onClick={handleDelete} color="error">
           Delete
         </Button>
         <Box sx={{ flex: 1 }} />
         <Button onClick={handleCancel} color="inherit">
           Cancel
         </Button>
        <Button
          key={`update-button-${forceUpdate}`}
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValidAndChanged}
          sx={{
            backgroundColor: "#627CFB",
            "&:hover": { backgroundColor: "#536DEC" },
          }}
        >
          Update
        </Button>
       </DialogActions>
     </Dialog>
   );
 }